import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Button, TextField, Alert, CircularProgress, Stack } from '@mui/material';
import { LockOutlined as LockIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import { aprovadorApi, AcessoInfo, AprovadorError } from '@/services/aprovador';

interface Props {
  token: string;
  info: AcessoInfo;
  onVerified: () => void;
}

/** Tela do código de 6 dígitos (primeiro acesso e a cada 24 h). */
const CodeGate: React.FC<Props> = ({ token, info, onVerified }) => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(info.retryInSeconds || 0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const enviar = async () => {
    setSending(true);
    setError(null);
    try {
      const r = await aprovadorApi.enviarCodigo(token);
      setSent(true);
      setCooldown(r.retryInSeconds);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) {
      const err = e as AprovadorError;
      if (err.retryInSeconds) { setCooldown(err.retryInSeconds); setSent(true); }
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const verificar = async (valor = code) => {
    if (valor.length !== 6) return;
    setChecking(true);
    setError(null);
    try {
      await aprovadorApi.verificar(token, valor);
      onVerified();
    } catch (e) {
      setError((e as AprovadorError).message);
      setCode('');
      inputRef.current?.focus();
    } finally {
      setChecking(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f3f5f8', p: 2 }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 400, p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LockIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>Olá{info.firstName ? `, ${info.firstName}` : ''}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {sent ? 'Digite o código de 6 dígitos enviado ao seu WhatsApp' : 'Para abrir o painel de pagamentos, vamos enviar um código ao seu WhatsApp'}
              {info.sentTo ? <> <b>{info.sentTo}</b></> : null}.
            </Typography>
          </Box>

          {!sent ? (
            <Button fullWidth size="large" variant="contained" startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
              disabled={sending} onClick={enviar}>
              Enviar código
            </Button>
          ) : (
            <>
              <TextField
                inputRef={inputRef}
                fullWidth
                value={code}
                placeholder="000000"
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(v);
                  if (v.length === 6) verificar(v);
                }}
                inputProps={{
                  inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6,
                  style: { textAlign: 'center', fontSize: 30, letterSpacing: 12, fontWeight: 600, fontFamily: 'monospace' },
                }}
                disabled={checking}
              />
              <Button fullWidth size="large" variant="contained" disabled={code.length !== 6 || checking} onClick={() => verificar()}>
                {checking ? <CircularProgress size={22} color="inherit" /> : 'Entrar'}
              </Button>
              <Button size="small" disabled={cooldown > 0 || sending} onClick={enviar}>
                {cooldown > 0 ? `Reenviar código em ${cooldown}s` : 'Reenviar código'}
              </Button>
            </>
          )}

          {error && <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>{error}</Alert>}
          <Typography variant="caption" color="text.secondary">
            O código vale 10 minutos. Depois de entrar, o acesso fica liberado por 24 horas neste aparelho.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CodeGate;
